import { GlobalStateController } from "bai-react-global-state";

import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { _ } from "js/vendor";
import { app } from "js/namespaces";
import { QuickStartTask } from "common/constants";

const logger = getLogger(LogGroup.CONTROLLERS);

interface QuickStartControllerState {
    isInitialized: boolean;
    quickStartTasks: Record<string, boolean>;
    isOpen: boolean;
    firstSmartSlideTaskDone: boolean;
    forceOpen: boolean;
}

const initialState: QuickStartControllerState = {
    isInitialized: false,
    quickStartTasks: {},
    isOpen: false,
    firstSmartSlideTaskDone: false,
    forceOpen: false
};

class QuickStartController extends GlobalStateController<QuickStartControllerState> {
    constructor(initialState: QuickStartControllerState) {
        super(initialState);
    }

    areAllTasksDone = () => {
        return Object.values(this.quickStartTasks || {}).every(task => task);
    }

    get quickStartTasks() {
        return this._state.quickStartTasks;
    }

    get forceOpen() {
        return this._state.forceOpen;
    }

    setForceOpen(forceOpen: boolean) {
        return this._updateState({ forceOpen });
    }

    get isInitialized() {
        return this._state.isInitialized;
    }

    setQuickStartTasks(quickStartTasks: Record<string, boolean>) {
        return this._updateState({ quickStartTasks });
    }

    // We need this to make sure we keep the correct state of the first smart slide task
    setAddYourFirstSmartSlideTask() {
        return this._updateState({ firstSmartSlideTaskDone: this._state.quickStartTasks[QuickStartTask.ADD_YOUR_FIRST_SMART_SLIDE] });
    }

    get firstSmartSlideTaskDone() {
        return this._state.firstSmartSlideTaskDone;
    }

    async initialize() {
        const user = AppController.user;

        if (!user) {
            logger.warn("[QuickStartController] initialize() - No user provided");
            return;
        }

        logger.info("[QuickStartController] initialize()", { userId: user.id });

        const quickStartTasks = user.get("quickStartTasks") || {};

        this.setQuickStartTasks(quickStartTasks);

        this._updateState({
            isInitialized: true
        });
    }

    async updateTask(taskKey: string, done: boolean) {
        const { quickStartTasks } = this._state;

        // we don't want to update it if the user doesn't have any quick start tasks
        if (_.isEmpty(quickStartTasks)) {
            return;
        }

        const user = AppController.user;

        if (!user) {
            logger.warn("[QuickStartController] updateTask() - No user available");
            return;
        }

        const updatedQuickStartTasks = {
            ...quickStartTasks,
            [taskKey]: done
        };

        try {
            user.update({ quickStartTasks: updatedQuickStartTasks });

            // Update local state immediately
            await this._updateState({ quickStartTasks: updatedQuickStartTasks });
        } catch (err) {
            logger.error(err, "[QuickStartController] updateTask() error updating user");
            // Revert local state on error
            await this._updateState({ quickStartTasks });
        }
    }

    toggleTask(taskLabel: string) {
        const currentDone = this.quickStartTasks[taskLabel] || false;
        return this.updateTask(taskLabel, !currentDone);
    }

    getTaskStatus(taskLabel: string): boolean {
        return this.quickStartTasks[taskLabel] || false;
    }

    async resetAllTasks() {
        const user = AppController.user;

        if (!user) {
            logger.warn("[QuickStartController] resetAllTasks() - No user available");
            return;
        }

        const currentQuickStartTasks = user.get("quickStartTasks") || {};
        const taskKeys = Object.keys(currentQuickStartTasks);

        // Create a new object with all tasks set to false
        const resetQuickStartTasks: Record<string, boolean> = {};
        taskKeys.forEach(key => {
            resetQuickStartTasks[key] = false;
        });

        // Update user model
        try {
            user.update({
                quickStartTasks: resetQuickStartTasks
            });

            // Update local state immediately
            await this._updateState({ quickStartTasks: resetQuickStartTasks });
        } catch (err) {
            logger.error(err, "[QuickStartController] resetAllTasks() error updating user");
            // Revert state on error
            await this._updateState({ quickStartTasks: currentQuickStartTasks });
        }
    }

    async reset() {
        logger.info("[QuickStartController] reset()");
        await this._updateState(() => _.cloneDeep(initialState));
    }
}

const quickStartController = new QuickStartController(_.cloneDeep(initialState));

// for lazy import and debug
app.quickStartController = quickStartController;

export default quickStartController;

